import { SvgIcon, SvgIconProps } from '@mui/material';
import { FC } from 'react';
import { colors } from '../../theme/colors';

type FileFamily = keyof typeof colors.fileType;

const FAMILY_BY_EXTENSION: Record<string, FileFamily> = {
  pdf: 'pdf',
  ppt: 'presentation',
  pptx: 'presentation',
  csv: 'sheet',
  xls: 'sheet',
  xlsx: 'sheet',
  xlsm: 'sheet',
  xltm: 'sheet',
  xltx: 'sheet',
  doc: 'word',
  docx: 'word',
  docm: 'word',
  dotm: 'word',
  dotx: 'word',
};

/** Longest extension that still fits on the badge at icon size. */
const MAX_LABEL_LENGTH = 4;

/** Lower-cased extension of a file name, or '' when it has none. */
export function fileExtension(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return dot > 0 && dot < fileName.length - 1 ? fileName.slice(dot + 1).toLowerCase() : '';
}

/** Badge text: the extension in caps when it fits, otherwise FILE. */
export function fileTypeLabel(fileName: string): string {
  const extension = fileExtension(fileName);
  return extension.length > 0 && extension.length <= MAX_LABEL_LENGTH ? extension.toUpperCase() : 'FILE';
}

export function fileTypeBadgeColor(fileName: string): string {
  return colors.fileType[FAMILY_BY_EXTENSION[fileExtension(fileName)] ?? 'other'];
}

export interface FileTypeIconProps extends Omit<SvgIconProps, 'children'> {
  fileName: string;
}

/** A page glyph with the file's type printed on a coloured badge, so a list of documents scans by type. */
export const FileTypeIcon: FC<FileTypeIconProps> = ({ fileName, ...svgProps }) => {
  const label = fileTypeLabel(fileName);
  return (
    <SvgIcon viewBox='0 0 24 24' data-file-type={label} {...svgProps}>
      <path d='M6 2h8l5 5v15H6z' fill={colors.neutral[10]} stroke={colors.neutral[30]} strokeWidth='0.75' />
      <path d='M14 2v5h5z' fill={colors.neutral[30]} />
      <rect x='3' y='12' width='18' height='8' rx='1.5' fill={fileTypeBadgeColor(fileName)} />
      <text
        x='12'
        y='18.3'
        textAnchor='middle'
        fontSize='5.6'
        fontWeight='700'
        letterSpacing='0.2'
        fill={colors.primary[0]}
      >
        {label}
      </text>
    </SvgIcon>
  );
};
